import { gql } from "@codesandbox/api";
import type {
  FragmentData,
  QueryData,
} from "@codesandbox/api/dist/esm/generated_gql";
import { useEnvironmentInterface } from "environment-interface";
import type { CsbApi } from "environment-interface/csbApi";
import { useCsbApi } from "hooks/api/useCsbApi";
import { useEffect, useState } from "react";
import type { Fetcher } from "swr";
import useSWR from "swr";

const addCollaboratorQuery = gql.createMutation(
  "AddCollaborator",
  ({
    id,
    username,
    authorization,
  }: {
    id: string;
    username: string;
    authorization: gql.Authorization;
  }) => ({
    addCollaborator: [
      { sandboxId: id, username, authorization },
      {
        id: true,
      },
    ],
  }),
);

export const useAddCollaborator = (sandboxId: string) => {
  const csbApi = useCsbApi();

  const [error, setError] = useState<Error | null>(null);
  const [fetching, setFetching] = useState(false);

  return [
    { fetching, error },
    async ({
      username,
      authorization,
    }: {
      username: string;
      authorization: gql.Authorization;
    }) => {
      setFetching(true);
      setError(null);

      return csbApi.gql
        .query(addCollaboratorQuery, { id: sandboxId, username, authorization })
        .catch((e) => {
          setError(e);
        })
        .finally(() => {
          setFetching(false);
        });
    },
  ] as const;
};

const inviteCollaboratorQuery = gql.createMutation(
  "InviteCollaborator",
  ({
    id,
    authorization,
    email,
  }: {
    id: string;
    authorization: gql.Authorization;
    email: string;
  }) => ({
    createSandboxInvitation: [
      { sandboxId: id, authorization, email },
      {
        id: true,
        authorization: true,
        email: true,
      },
    ],
  }),
);

export const useInviteCollaborator = (sandboxId: string) => {
  const csbApi = useCsbApi();

  const [error, setError] = useState<Error | null>(null);
  const [fetching, setFetching] = useState(false);

  return [
    { fetching, error },
    async ({
      email,
      authorization,
    }: {
      email: string;
      authorization: gql.Authorization;
    }) => {
      setFetching(true);
      setError(null);

      return csbApi.gql
        .query(inviteCollaboratorQuery, { id: sandboxId, email, authorization })
        .catch((e) => {
          setError(e);
        })
        .finally(() => {
          setFetching(false);
        });
    },
  ] as const;
};

const changeSandboxInvitationAuthorizationQuery = gql.createMutation(
  "ChangeSandboxInvitationAuthorization",
  ({
    id,
    authorization,
    invitationId,
  }: {
    id: string;
    authorization: gql.Authorization;
    invitationId: string;
  }) => ({
    changeSandboxInvitationAuthorization: [
      { sandboxId: id, authorization, invitationId },
      {
        id: true,
        authorization: true,
        email: true,
      },
    ],
  }),
);

export const useChangeInvitationAuthorization = (sandboxId: string) => {
  const csbApi = useCsbApi();

  const [error, setError] = useState<Error | null>(null);
  const [fetching, setFetching] = useState(false);

  return [
    { fetching, error },
    async ({
      invitationId,
      authorization,
    }: {
      invitationId: string;
      authorization: gql.Authorization;
    }) => {
      setFetching(true);
      setError(null);

      return csbApi.gql
        .query(changeSandboxInvitationAuthorizationQuery, {
          id: sandboxId,
          invitationId,
          authorization,
        })
        .catch((e) => {
          setError(e);
        })
        .finally(() => {
          setFetching(false);
        });
    },
  ] as const;
};

const revokeSandboxInvitationQuery = gql.createMutation(
  "RevokeSandboxInvitation",
  ({ id, invitationId }: { id: string; invitationId: string }) => ({
    revokeSandboxInvitation: [
      { sandboxId: id, invitationId },
      {
        id: true,
        authorization: true,
        email: true,
      },
    ],
  }),
);

export const useRevokeCollaboratorInvitation = (sandboxId: string) => {
  const csbApi = useCsbApi();

  const [error, setError] = useState<Error | null>(null);
  const [fetching, setFetching] = useState(false);

  return [
    { fetching, error },
    async (invitationId: string) => {
      setFetching(true);
      setError(null);

      return csbApi.gql
        .query(revokeSandboxInvitationQuery, {
          id: sandboxId,
          invitationId,
        })
        .catch((e) => {
          setError(e);
        })
        .finally(() => {
          setFetching(false);
        });
    },
  ] as const;
};

const changeCollaboratorAuthorizationQuery = gql.createMutation(
  "ChangeCollaboratorAuthorization",
  ({
    id,
    authorization,
    username,
  }: {
    id: string;
    authorization: gql.Authorization;
    username: string;
  }) => ({
    changeCollaboratorAuthorization: [
      { sandboxId: id, authorization, username },
      {
        id: true,
      },
    ],
  }),
);

export const useChangeCollaboratorAuthorization = (sandboxId: string) => {
  const csbApi = useCsbApi();

  const [error, setError] = useState<Error | null>(null);
  const [fetching, setFetching] = useState(false);

  return [
    { fetching, error },
    async ({
      username,
      authorization,
    }: {
      username: string;
      authorization: gql.Authorization;
    }) => {
      setFetching(true);
      setError(null);

      return csbApi.gql
        .query(changeCollaboratorAuthorizationQuery, {
          id: sandboxId,
          username,
          authorization,
        })
        .catch((e) => {
          setError(e);
        })
        .finally(() => {
          setFetching(false);
        });
    },
  ] as const;
};

const removeCollaboratorQuery = gql.createMutation(
  "RemoveCollaboartor",
  ({ id, username }: { id: string; username: string }) => ({
    removeCollaborator: [
      { sandboxId: id, username },
      {
        id: true,
      },
    ],
  }),
);

export const useRemoveCollaborator = (sandboxId: string) => {
  const csbApi = useCsbApi();

  const [error, setError] = useState<Error | null>(null);
  const [fetching, setFetching] = useState(false);

  return [
    { fetching, error },
    async (username: string) => {
      setFetching(true);
      setError(null);

      return csbApi.gql
        .query(removeCollaboratorQuery, {
          id: sandboxId,
          username,
        })
        .catch((e) => {
          setError(e);
        })
        .finally(() => {
          setFetching(false);
        });
    },
  ] as const;
};

const collaboratorFragment = gql.fragments.Collaborator({
  id: true,
  user: {
    id: true,
    name: true,
    username: true,
    avatarUrl: true,
  },
  authorization: true,
});

export type CollaboratorFragment = FragmentData<typeof collaboratorFragment>;

const invitationFragment = gql.fragments.Invitation({
  id: true,
  authorization: true,
  email: true,
});

export type InvitationFragment = FragmentData<typeof invitationFragment>;

const sandboxCollaboratorsQuery = gql.createQuery(
  "SandboxCollaborators",
  ({ id }: { id: string }) => ({
    sandbox: [
      { sandboxId: id },
      {
        id: true,
        collaborators: collaboratorFragment,
        invitations: invitationFragment,
      },
    ],
  }),
);

export const useSandboxCollaborators = (sandboxId: string) => {
  const csbApi = useCsbApi();

  const [error, setError] = useState<Error | null>(null);
  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState<
    QueryData<typeof sandboxCollaboratorsQuery> | undefined
  >(undefined);

  function fetch() {
    setFetching(true);
    setError(null);
    csbApi.gql
      .query(sandboxCollaboratorsQuery, {
        id: sandboxId,
      })
      .then(setData)
      .catch((e) => {
        setError(e);
      })
      .finally(() => {
        setFetching(false);
      });
  }

  useEffect(fetch, []);

  return [{ fetching, error, data }, fetch] as const;
};

type UsersRestResponse = Array<{
  id: string;
  avatar_url: string;
  username: string;
}>;

/**
 * Fetches users by username from the REST API
 * Returns an empty array if it's passed a string of less than 3 characters
 */
const searchUsers =
  (csbApi: CsbApi | undefined): Fetcher<UsersRestResponse, [string, string]> =>
  async (_, username) => {
    if (username.length < 3) {
      return Promise.resolve([]);
    }

    const isEmail = username.includes("@");
    if (isEmail) {
      return Promise.resolve([]);
    }

    const users = await csbApi?.rest.get({
      path: "/v1/users/search",
      query: {
        username,
      },
    });

    return users as UsersRestResponse;
  };

export const useSearchUsersByName = (username: string) => {
  const { csbApi } = useEnvironmentInterface();

  return useSWR(["users/search", username], searchUsers(csbApi));
};
